/**
 * EF-DOCUMENT-REVIEW-UX-AND-DOCUMENT-SHARING-V1: Send Documents Dialog
 * 
 * Allows administrators to share crew documents with external contacts:
 * - Select documents to send
 * - Choose recipient from System Contacts
 * - Compose email message
 * - Choose delivery method (email attachment or secure link)
 * - All sharing logged in audit trail
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { ScrollArea } from "../ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Send,
  Mail,
  Link2,
  FileText,
  Building2,
  User,
  Clock,
  Loader2,
  Check,
  AlertTriangle,
  Calendar,
  Shield,
  ExternalLink,
  Copy,
  CheckCircle2,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { cn } from "../../lib/utils";

// Expiry options for secure links
const EXPIRY_OPTIONS = [
  { value: "24h", label: "24 hours", description: "Short-term access" },
  { value: "72h", label: "72 hours", description: "Extended access" },
  { value: "7d", label: "7 days", description: "Full week access" },
];

// Document type icons/colors
const getDocTypeStyle = (docType) => {
  const types = {
    national_id: { color: "text-blue-400 bg-blue-500/10 border-blue-500/20", label: "ID Card" },
    passport: { color: "text-purple-400 bg-purple-500/10 border-purple-500/20", label: "Passport" },
    carte_vitale: { color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20", label: "Carte Vitale" },
    driver_license: { color: "text-amber-400 bg-amber-500/10 border-amber-500/20", label: "Driver License" },
    cv: { color: "text-cyan-400 bg-cyan-500/10 border-cyan-500/20", label: "CV" },
    contract: { color: "text-pink-400 bg-pink-500/10 border-pink-500/20", label: "Contract" },
    default: { color: "text-gray-400 bg-gray-500/10 border-gray-500/20", label: "Document" },
  };
  return types[docType] || types.default;
};

// Category config for contacts
const getCategoryStyle = (category) => {
  const styles = {
    government: { icon: "text-blue-400", label: "Government" },
    client: { icon: "text-emerald-400", label: "Client" },
    venue: { icon: "text-amber-400", label: "Venue" },
    other: { icon: "text-purple-400", label: "Other" },
  };
  return styles[category] || styles.other;
};

// Main Component
export default function SendDocumentsDialog({
  open,
  onOpenChange,
  staffId,
  staffName,
  documents = [],
  onSuccess,
}) {
  // State
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState("");
  const [method, setMethod] = useState("email_attachment");
  const [linkExpiry, setLinkExpiry] = useState("72h");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [shareResult, setShareResult] = useState(null);
  
  // Load contacts
  useEffect(() => {
    const loadContacts = async () => {
      if (!open) return;
      try {
        setLoadingContacts(true);
        const response = await api.get("/system-contacts");
        setContacts(response.data || []);
      } catch (err) {
        console.error("Failed to load contacts:", err);
        toast.error("Failed to load contacts");
      } finally {
        setLoadingContacts(false);
      }
    };
    loadContacts();
  }, [open]);
  
  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedDocIds([]);
      setSelectedContactId("");
      setMethod("email_attachment");
      setLinkExpiry("72h");
      setSubject(`Identity Documents - ${staffName || "Crew Member"}`);
      setMessage(generateDefaultMessage());
      setShareResult(null);
    }
  }, [open, staffName]);
  
  // Generate default message
  const generateDefaultMessage = useCallback(() => {
    return `Dear Recipient,

Please find attached the requested identity documents for ${staffName || "the crew member"}.

These documents are being shared for verification purposes.

Best regards,
EventsFlo Administration`;
  }, [staffName]);
  
  // Toggle document selection
  const toggleDocument = (docId) => {
    setSelectedDocIds(prev => 
      prev.includes(docId)
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };
  
  // Select all documents
  const selectAllDocuments = () => {
    if (selectedDocIds.length === documents.length) {
      setSelectedDocIds([]);
    } else {
      setSelectedDocIds(documents.map(d => d.id));
    }
  };
  
  // Filter contacts by search
  const filteredContacts = contacts.filter(contact => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      contact.company_name?.toLowerCase().includes(term) ||
      contact.contact_name?.toLowerCase().includes(term) ||
      contact.email?.toLowerCase().includes(term)
    );
  });
  
  // Get selected contact
  const selectedContact = contacts.find(c => c.id === selectedContactId);
  
  // Handle send
  const handleSend = async () => {
    if (selectedDocIds.length === 0) {
      toast.error("Please select at least one document");
      return;
    }
    
    if (!selectedContactId) {
      toast.error("Please select a recipient");
      return;
    }
    
    if (!subject.trim()) {
      toast.error("Please enter an email subject");
      return;
    }
    
    try {
      setSending(true);
      
      const payload = {
        document_ids: selectedDocIds,
        contact_id: selectedContactId,
        method: method,
        subject: subject,
        message: message,
        link_expiry: method === "secure_link" ? linkExpiry : null,
      };
      
      const response = await api.post(`/staff/${staffId}/send-documents`, payload);
      
      setShareResult(response.data);
      
      if (method === "email_attachment") {
        toast.success(`Documents sent to ${selectedContact?.email}`);
        setTimeout(() => {
          onOpenChange(false);
          onSuccess?.();
        }, 2000);
      } else {
        // Show secure link result
        toast.success("Secure link generated");
      }
      
    } catch (err) {
      console.error("Send documents error:", err);
      toast.error(err.response?.data?.detail || "Failed to send documents");
    } finally {
      setSending(false);
    }
  };
  
  // Copy secure link
  const copySecureLink = async () => {
    if (shareResult?.secure_link) {
      try {
        await navigator.clipboard.writeText(shareResult.secure_link);
        toast.success("Link copied to clipboard");
      } catch {
        toast.error("Failed to copy link");
      }
    }
  };
  
  // Current documents with status info
  const currentDocuments = documents.filter(d => d.is_current !== false && d.status !== "superseded");
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5 text-primary" />
            Send Documents
          </DialogTitle>
          <DialogDescription>
            Share identity documents for <strong>{staffName}</strong> with external contacts
          </DialogDescription>
        </DialogHeader>
        
        {/* Success Result - Secure Link */}
        {shareResult && method === "secure_link" ? (
          <div className="space-y-4 p-4">
            <div className="flex items-center justify-center gap-2 text-emerald-400">
              <CheckCircle2 className="h-8 w-8" />
              <span className="text-lg font-medium">Secure Link Generated</span>
            </div>
            
            <div className="p-4 bg-muted/30 rounded-lg border border-white/10">
              <Label className="text-xs text-muted-foreground">Secure Download Link</Label>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  readOnly
                  value={shareResult.secure_link || ""}
                  className="font-mono text-sm"
                />
                <Button variant="outline" size="sm" onClick={copySecureLink}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                <Clock className="h-3 w-3 inline mr-1" />
                Expires: {shareResult.expires_at ? new Date(shareResult.expires_at).toLocaleString() : "N/A"}
              </p>
            </div>
            
            <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-amber-400" />
              <p className="text-sm text-amber-300">
                Share this link securely. The recipient can download the documents until the link expires.
              </p>
            </div>
            
            <DialogFooter>
              <Button onClick={() => { onOpenChange(false); onSuccess?.(); }}>
                Done
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <ScrollArea className="flex-1 max-h-[60vh]">
            <div className="space-y-6 p-4">
              {/* Step 1: Select Documents */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <FileText className="h-4 w-4 text-primary" />
                    1. Select Documents
                  </Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={selectAllDocuments}
                    className="text-xs"
                  >
                    {selectedDocIds.length === currentDocuments.length ? "Deselect All" : "Select All"}
                  </Button>
                </div>
                
                {currentDocuments.length === 0 ? (
                  <div className="p-4 text-center text-muted-foreground bg-muted/20 rounded-lg">
                    <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No documents available</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {currentDocuments.map(doc => {
                      const style = getDocTypeStyle(doc.document_type);
                      const isSelected = selectedDocIds.includes(doc.id);
                      
                      return (
                        <div
                          key={doc.id}
                          onClick={() => toggleDocument(doc.id)}
                          className={cn(
                            "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all",
                            isSelected
                              ? "bg-primary/10 border-primary/40"
                              : "bg-muted/20 border-white/10 hover:border-white/30"
                          )}
                          data-testid={`doc-select-${doc.id}`}
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleDocument(doc.id)}
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className={style.color}>
                                {style.label}
                              </Badge>
                              {doc.document_number && (
                                <span className="text-xs text-muted-foreground font-mono">
                                  {doc.document_number}
                                </span>
                              )}
                            </div>
                            <p className="text-sm mt-1">{doc.original_filename || "Document"}</p>
                          </div>
                          {doc.status === "verified" && (
                            <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                              <Check className="h-3 w-3 mr-1" />
                              Verified
                            </Badge>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              
              {/* Step 2: Select Recipient */}
              <div className="space-y-3">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-primary" />
                  2. Select Recipient
                </Label>
                
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search contacts..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                {loadingContacts ? (
                  <div className="flex items-center justify-center h-24">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                ) : filteredContacts.length === 0 ? (
                  <div className="p-4 text-center text-muted-foreground bg-muted/20 rounded-lg">
                    <Building2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No contacts found</p>
                    <p className="text-xs mt-1">Add contacts in System Contacts page</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {filteredContacts.map(contact => {
                      const isSelected = selectedContactId === contact.id;
                      const catStyle = getCategoryStyle(contact.category);
                      
                      return (
                        <div
                          key={contact.id}
                          onClick={() => setSelectedContactId(contact.id)}
                          className={cn(
                            "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all",
                            isSelected
                              ? "bg-primary/10 border-primary/40"
                              : "bg-muted/20 border-white/10 hover:border-white/30"
                          )}
                          data-testid={`contact-select-${contact.id}`}
                        >
                          <div className={cn("w-2 h-2 rounded-full", catStyle.icon.replace("text-", "bg-"))} />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{contact.company_name}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {contact.contact_name && `${contact.contact_name} · `}
                              {contact.email}
                            </p>
                          </div>
                          {isSelected && <Check className="h-5 w-5 text-primary" />}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              
              {/* Step 3: Delivery Method */}
              <div className="space-y-3">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <Mail className="h-4 w-4 text-primary" />
                  3. Delivery Method
                </Label>
                
                <Tabs value={method} onValueChange={setMethod}>
                  <TabsList className="w-full">
                    <TabsTrigger value="email_attachment" className="flex-1 gap-2">
                      <Mail className="h-4 w-4" />
                      Email Attachment
                    </TabsTrigger>
                    <TabsTrigger value="secure_link" className="flex-1 gap-2">
                      <Link2 className="h-4 w-4" />
                      Secure Link
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="email_attachment" className="mt-3">
                    <div className="p-3 bg-muted/20 rounded-lg border border-white/10">
                      <div className="flex items-start gap-2">
                        <Mail className="h-4 w-4 text-primary mt-0.5" />
                        <div>
                          <p className="text-sm">Send documents as email attachments</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Files will be attached directly to the email sent to {selectedContact?.email || "the recipient"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="secure_link" className="mt-3 space-y-3">
                    <div className="p-3 bg-muted/20 rounded-lg border border-white/10">
                      <div className="flex items-start gap-2">
                        <Shield className="h-4 w-4 text-primary mt-0.5" />
                        <div>
                          <p className="text-sm">Generate a time-limited download link</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            The recipient receives a secure link that expires after the selected period
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-xs">Link Expiry</Label>
                      <Select value={linkExpiry} onValueChange={setLinkExpiry}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {EXPIRY_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>
                              <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4" />
                                <span>{opt.label}</span>
                                <span className="text-xs text-muted-foreground">- {opt.description}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
              
              {/* Step 4: Subject & Message */}
              <div className="space-y-3">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  4. Email Subject & Message
                </Label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Email subject..."
                  className="text-sm"
                  data-testid="email-subject"
                />
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Compose your message..."
                  rows={5}
                  className="resize-none"
                />
              </div>
            </div>
          </ScrollArea>
        )}
        
        {/* Footer - only show when not showing result */}
        {!shareResult && (
          <DialogFooter className="border-t border-white/10 pt-4">
            <div className="flex items-center justify-between w-full">
              <div className="text-sm text-muted-foreground">
                {selectedDocIds.length > 0 && (
                  <span>
                    {selectedDocIds.length} document{selectedDocIds.length > 1 ? "s" : ""} selected
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSend}
                  disabled={sending || selectedDocIds.length === 0 || !selectedContactId}
                  data-testid="send-documents-btn"
                >
                  {sending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : method === "email_attachment" ? (
                    <Mail className="h-4 w-4 mr-2" />
                  ) : (
                    <Link2 className="h-4 w-4 mr-2" />
                  )}
                  {method === "email_attachment" ? "Send Email" : "Generate Link"}
                </Button>
              </div>
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
